import { useTheme } from '@emotion/react'
import {
    isPublicationCardItem,
    PublicationCardItemWithVideo,
    VideoQueueItemMeta,
} from '@news-mono/web-common'
import { MaybeLoaded } from '@west-australian-newspapers/json-react-layouts-data-loader'
import React, { useEffect, useRef } from 'react'
import {
    applyContainerRatio,
    ResponsiveContainer,
} from '../../content/Picture/responsive'
import { IconVideo2 } from '../../icons/IconVideo2/IconVideo2'
import { CommonCardProps } from '../CardItem.Props'
import { StyledVideoCardNowPlaying } from '../CardMedia/StyledMedia.styled'
import { StyledIconVideo2 } from '../Kicker/Kicker'
import { StyledKicker, StyledKickerText } from '../Kicker/Kicker.styled'
import {
    StyledBauhausVideoCard,
    StyledBauhausVideoCardMedia,
    StyledBauhausVideoCardMediaWrapper,
    StyledBauhausVideoCardText,
    StyledBauhausVideoCardTextMedia,
    StyledBauhausVideoCardTextWrapper,
} from './BauhausVideoCard.styled'

export interface BauhausVideoHubCardProps
    extends CommonCardProps,
        ResponsiveContainer {
    videoId?: string
    title?: string
    topics?: string
    url?: string
    posterImage?: {
        width: number
        height: number
        location: {
            url: string
            kind: string
        }
    }
    isCurrentlyPlaying?: boolean
    item: MaybeLoaded<PublicationCardItemWithVideo>
    itemMeta: VideoQueueItemMeta
    handleCarouselSelection: (offsetTop?: number) => void
    handleSwipperSelection: () => void
    onSelect: (item: VideoQueueItemMeta) => void
}

export const BauhausVideoHubCard: React.FC<BauhausVideoHubCardProps> = ({
    containerWidthRatios,
    onEvent,
    isCurrentlyPlaying,
    item,
    handleCarouselSelection,
    handleSwipperSelection,
    onSelect,
    itemMeta,
}) => {
    const theme = useTheme()
    const cardRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (isCurrentlyPlaying) {
            handleCarouselSelection(cardRef.current?.offsetTop)
            handleSwipperSelection()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCurrentlyPlaying])

    if (item.loaded && isPublicationCardItem(item.result)) {
        const handleClick = () => {
            onSelect(itemMeta)
            handleCarouselSelection(cardRef.current?.offsetTop)
        }
        const handleKeyDown = (event: any) => {
            if (event.key === 'Enter') {
                onSelect(itemMeta)
                handleCarouselSelection(cardRef.current?.offsetTop)
            }
        }

        return (
            <StyledBauhausVideoCard
                isCurrentlyPlaying={isCurrentlyPlaying}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                ref={cardRef}
                tabIndex={0}
            >
                <StyledBauhausVideoCardTextMedia>
                    <StyledBauhausVideoCardMediaWrapper>
                        <StyledBauhausVideoCardTextWrapper>
                            {theme.kind === 'thewest' ? (
                                <StyledKicker>
                                    {item.result.primaryTopic.title}
                                    <StyledKickerText>
                                        <IconVideo2
                                            width={14}
                                            height={9}
                                            horizontalSpacing={6}
                                            className={StyledIconVideo2}
                                        />
                                    </StyledKickerText>
                                </StyledKicker>
                            ) : (
                                <StyledKicker>
                                    {item.result.primaryTopic.title}
                                </StyledKicker>
                            )}

                            <StyledBauhausVideoCardText
                                item={{
                                    loaded: true,
                                    result: item.result,
                                }}
                                onEvent={onEvent}
                                teaserMode={'hidden'}
                                fontScale={1}
                                kickerMode={'hidden'}
                                isCurrentlyPlaying={isCurrentlyPlaying}
                            />
                            {isCurrentlyPlaying && (
                                <StyledVideoCardNowPlaying>
                                    Now Playing
                                </StyledVideoCardNowPlaying>
                            )}
                        </StyledBauhausVideoCardTextWrapper>
                        <StyledBauhausVideoCardMedia
                            item={{
                                loaded: true,
                                result: item.result,
                            }}
                            onEvent={onEvent}
                            imageWidths={applyContainerRatio(
                                containerWidthRatios,
                                {
                                    mobile: '100vw',
                                    tablet: '100vw',
                                    desktop: `${theme.siteMetrics.mainContentWidth}px`,
                                    fallbackWidth:
                                        theme.siteMetrics.mainContentWidth,
                                },
                            )}
                            fixedRatio={'16:9'}
                        />
                    </StyledBauhausVideoCardMediaWrapper>
                </StyledBauhausVideoCardTextMedia>
            </StyledBauhausVideoCard>
        )
    }
    return null
}
